/**
 * Cart drawer Offers tab.
 *
 * 1. Lists every live Windfall offer (contract status "active"), including the ones whose
 *    "show in cart" toggle is off, plus the theme's free-delivery tier when the tiered bar
 *    shows it. Reads the same contract as the cart's tiered bar (window.Windfall.getContract(),
 *    falling back to the embed's bootstrap object) and the embed's evaluated state
 *    (window.Windfall.getState()) when it covers an offer.
 * 2. Shows the Offers tab dot only when something is unseen. The seen set lives in
 *    localStorage (sa_offers_seen) and is written when the Offers tab is viewed.
 *
 * The drawer section re-renders on every cart change (replaceChildren), so everything here is
 * global and re-applies itself to whatever drawer DOM is current.
 */
(() => {
  const key = '__cartOffersTab';
  if (window[key]) {
    window[key].refresh();
    return;
  }

  const STORAGE_KEY = 'sa_offers_seen';
  const SPEND_TYPES = ['free_shipping', 'percent_off', 'amount_off', 'spend_gift'];
  const GIFT_TYPES = ['spend_gift', 'combo_gift'];

  let liveSignature = null;
  let renderRequest = 0;
  let scheduled = false;

  // ---------------------------------------------------------------- storage (never throws)

  function readSeen() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  function writeSeen(signature) {
    try {
      window.localStorage.setItem(STORAGE_KEY, signature);
    } catch {
      // Private mode / blocked storage: the dot simply clears for this page view.
    }
  }

  // ---------------------------------------------------------------- helpers

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[char]);

  const numericId = (gid) => String(gid ?? '').split('/').pop();

  function isGiftLine(item) {
    const props = item?.properties || {};
    return props._sow_gift === true || props._sow_gift === 'true'
      || props._auto_free_gift === true || props._auto_free_gift === 'true';
  }

  // Same qualifying subtotal as windfall-offers.js / windfall-offers.liquid (pence).
  function qualifyingSubtotalCents(cart) {
    return (cart?.items || []).reduce((sum, item) => {
      if (isGiftLine(item) || !item.requires_shipping) return sum;
      return sum + (item.original_line_price ?? item.final_line_price ?? 0);
    }, 0);
  }

  function isLive(offer) {
    if (offer?.status !== 'active') return false;
    const now = Date.now();
    if (offer.startsAt && Date.parse(offer.startsAt) > now) return false;
    if (offer.endsAt && Date.parse(offer.endsAt) <= now) return false;
    return true;
  }

  function qualifiersOf(group) {
    return group?.anyOf ? group.anyOf : [group];
  }

  function isSpendGroup(group) {
    const qualifiers = qualifiersOf(group);
    return qualifiers.length > 0 && qualifiers.every((q) => q?.source === 'cart_value');
  }

  // goal = max(threshold, any cart_value amount), in shop-currency pence — mirrors the bar.
  function goalCents(offer) {
    let goal = offer.threshold?.amount ? Math.round(Number(offer.threshold.amount) * 100) : 0;
    for (const group of offer.requires?.allOf || []) {
      for (const q of qualifiersOf(group)) {
        if (q?.source === 'cart_value') goal = Math.max(goal, Math.round(Number(q.amount) * 100));
      }
    }
    return goal;
  }

  // First-paint fallback for when the embed has not evaluated an offer yet.
  function groupSatisfied(group, ctx) {
    return qualifiersOf(group).some((q) => {
      switch (q?.source) {
        case 'cart':
          return [...(q.productIds || []), ...(q.resolvedProductIds || [])]
            .some((id) => ctx.productIds.has(numericId(id)));
        case 'cart_value':
          return ctx.subtotal >= Math.round(Number(q.amount) * 100) * ctx.rate;
        case 'subscription_in_cart':
          return ctx.hasSellingPlan;
        case 'authenticated':
          return Boolean(ctx.customer?.loggedIn);
        case 'customer_metafield':
          return q.key === 'has_omega_subscription' && Boolean(ctx.customer?.hasOmegaSubscription);
        default:
          return false;
      }
    });
  }

  function rewardPhrase(offer) {
    switch (offer.type) {
      case 'free_shipping': return 'free delivery';
      case 'percent_off': return offer.percent ? `${offer.percent}% off` : (offer.label || offer.discountTitle);
      case 'spend_gift':
      case 'combo_gift': return 'the free gift'; // the row title already names the gift
      default: return offer.label || offer.discountTitle || '';
    }
  }

  const joinAnd = (list) => list.length <= 1 ? list.join('') : `${list.slice(0, -1).join(', ')} and ${list.at(-1)}`;

  // ---------------------------------------------------------------- evaluation

  function evaluate(offer, ctx) {
    const evaluated = ctx.stateById[offer.id];
    const reward = rewardPhrase(offer);
    const giftTitle = (offer.gift?.displayTitle || '').trim() || offer.gift?.title || '';
    const title = GIFT_TYPES.includes(offer.type) && giftTitle
      ? `Free ${giftTitle}`
      : (offer.label || offer.discountTitle || '');
    const row = {
      id: offer.id,
      title,
      image: offer.gift?.imageUrl || null,
      icon: GIFT_TYPES.includes(offer.type) ? 'gift' : offer.type === 'free_shipping' ? 'truck' : 'discount',
      unlocked: false,
      jump: false, // available without a threshold; the row points at the offer blocks below
      unavailable: evaluated?.state === 'unavailable',
      progress: 0,
      remaining: Infinity,
      message: '',
    };

    // ---- quick discounts: per-product sale prices already listed in the offer blocks below, nothing to
    //      unlock. The contract carries only the internal name (label/discountTitle "Quick discounts"), so
    //      the customer-facing title comes from the section setting (data-quick-discount-title).
    if (offer.type === 'quick_discount') {
      row.title = ctx.quickDiscountTitle || title;
      const items = offer.items || [];
      if (!items.length) return row;
      const variants = new Set(items.map((item) => numericId(item.variantId)));
      const products = new Set(items.map((item) => numericId(item.productId)));
      const inCart = ctx.cart.items.filter((item) => !isGiftLine(item) && variants.has(String(item.variant_id)));
      const quantity = inCart.reduce((sum, item) => sum + item.quantity, 0);
      row.unlocked = quantity > 0;
      row.jump = !row.unlocked;
      row.message = row.unlocked
        ? `Saving on ${quantity} ${quantity === 1 ? 'item' : 'items'} in your cart`
        : `Reduced prices on ${products.size} ${products.size === 1 ? 'product' : 'products'}, see below`;
      return row;
    }

    const groups = offer.requires?.allOf || [];
    const goal = goalCents(offer) * ctx.rate;
    const hasSpend = goal > 0 && (SPEND_TYPES.includes(offer.type) || groups.some(isSpendGroup));
    const nonSpendGroups = groups.filter((group) => !isSpendGroup(group));

    // Unlocked: embed state wins (applied beats state, see windfall-offers.js), else first-paint math.
    if (evaluated) {
      row.unlocked = evaluated.state === 'unlocked' || evaluated.state === 'applied' || evaluated.applied === true;
    } else {
      const spendOk = !hasSpend || ctx.subtotal >= goal;
      row.unlocked = spendOk && nonSpendGroups.every((group) => groupSatisfied(group, ctx));
    }

    // Missing (non-spend) requirement labels, straight from the contract's group labels.
    const spendLabels = new Set(groups.filter(isSpendGroup).map((group) => group.label));
    const missingLabels = evaluated
      ? (evaluated.missing || []).filter((label) => label && !spendLabels.has(label))
      : nonSpendGroups.filter((group) => !groupSatisfied(group, ctx)).map((group) => group.label).filter(Boolean);

    const remaining = hasSpend ? Math.max(0, goal - ctx.subtotal) : 0;
    if (hasSpend) row.remaining = remaining;
    const totalSteps = (hasSpend ? 1 : 0) + nonSpendGroups.length;
    if (totalSteps) {
      const spendFrac = hasSpend ? Math.min(1, ctx.subtotal / goal) : 0;
      row.progress = (spendFrac + (nonSpendGroups.length - missingLabels.length)) / totalSteps;
    }

    if (row.unavailable) {
      row.message = GIFT_TYPES.includes(offer.type) ? 'Gift out of stock' : 'Currently unavailable';
      return row;
    }

    if (row.unlocked) {
      if (offer.type === 'free_shipping') row.message = 'Free delivery unlocked';
      else if (GIFT_TYPES.includes(offer.type)) row.message = evaluated?.giftInCart || evaluated?.state === 'applied' ? 'Unlocked, free in your cart' : 'Unlocked, adding to your cart';
      else row.message = evaluated?.applied || evaluated?.state === 'applied' ? `${reward.charAt(0).toUpperCase()}${reward.slice(1)} applied` : `Unlocked, ${reward} at checkout`;
      return row;
    }

    if (!hasSpend && !groups.length) return row; // contract gives no thresholds: title only

    const money = ctx.format(remaining);
    if (hasSpend && remaining > 0 && missingLabels.length) {
      row.message = `Add ${joinAnd(missingLabels)} and spend ${money} more for ${reward}`;
    } else if (hasSpend && remaining > 0) {
      row.message = ctx.subtotal > 0 ? `Add ${money} more for ${reward}` : `Spend ${ctx.format(goal)} for ${reward}`;
    } else if (missingLabels.length) {
      row.message = `Add ${joinAnd(missingLabels)} to get ${reward}`;
    }
    return row;
  }

  function buildRows(root, cart) {
    const contract = window.Windfall?.getContract?.() || window.__WINDFALL_BOOTSTRAP__?.contract || null;
    const state = window.Windfall?.getState?.() || null;
    const bootstrap = window.__WINDFALL_BOOTSTRAP__ || {};
    const currency = cart.currency || contract?.currency || 'GBP';
    const locale = document.documentElement.lang || 'en-GB';
    const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
    const wholeFormatter = new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const ctx = {
      cart,
      rate: Number(window.Shopify?.currency?.rate || 1),
      subtotal: qualifyingSubtotalCents(cart),
      productIds: new Set(cart.items.filter((item) => !isGiftLine(item)).map((item) => String(item.product_id))),
      hasSellingPlan: cart.items.some((item) => item.selling_plan_allocation),
      customer: bootstrap.customer || null,
      quickDiscountTitle: (root.dataset.quickDiscountTitle || '').trim(),
      stateById: Object.fromEntries((state?.offers || []).map((offer) => [offer.id, offer])),
      format: (cents) => {
        const rounded = Math.round(cents);
        return (rounded % 100 === 0 ? wholeFormatter : formatter).format(rounded / 100);
      },
    };

    const liveOffers = (contract?.offers || []).filter(isLive);
    const rows = liveOffers.map((offer) => evaluate(offer, ctx));

    // Theme free-delivery tier (first segment of the cart's tiered bar). It yields to a
    // contract-published tiered free_shipping offer, exactly like windfall-offers.liquid.
    const themeShipping = Number(root.dataset.themeShippingCents || 0) * ctx.rate;
    const contractShipping = liveOffers.some((offer) => offer.type === 'free_shipping' && offer.ui?.progressTier && offer.ui?.showInCart !== false);
    if (themeShipping > 0 && !contractShipping) {
      const unlocked = ctx.subtotal >= themeShipping;
      const remaining = themeShipping - ctx.subtotal;
      rows.push({
        id: 'theme-free-shipping',
        title: 'Free delivery',
        image: null,
        icon: 'truck',
        unlocked,
        unavailable: false,
        progress: Math.min(1, ctx.subtotal / themeShipping),
        remaining: Math.max(0, remaining),
        message: unlocked
          ? 'Free delivery unlocked'
          : ctx.subtotal > 0 ? `Add ${ctx.format(remaining)} more for free delivery` : `Spend ${ctx.format(themeShipping)} for free delivery`,
      });
    }

    // Unlocked first, then always-available rows, then locked by closeness; unavailable and title-only rows last.
    const rank = (row) => (row.unlocked ? 0 : row.jump ? 1 : row.unavailable ? 4 : row.message ? 2 : 3);
    rows.sort((a, b) => rank(a) - rank(b)
      || b.progress - a.progress
      || (a.remaining === b.remaining ? 0 : a.remaining < b.remaining ? -1 : 1)
      || a.title.localeCompare(b.title));
    return { rows, contractLoaded: Boolean(contract) };
  }

  // ---------------------------------------------------------------- rendering

  function iconMarkup(root, name) {
    return root.querySelector(`[data-cart-offers-icon="${name}"]`)?.innerHTML || '';
  }

  function jumpTarget() {
    return document.querySelector('#cart-drawer .cart-offers');
  }

  function rowMarkup(root, row) {
    const media = row.image
      ? `<img class="cart-offers-list__media" src="${escapeHtml(row.image)}" alt="" width="44" height="44" loading="lazy">`
      : `<span class="cart-offers-list__icon" aria-hidden="true">${iconMarkup(root, row.icon)}</span>`;
    const canJump = row.jump && Boolean(jumpTarget());
    let status = '';
    if (row.unlocked) {
      status = `<span class="cart-offers-list__state cart-offers-list__state--unlocked">${iconMarkup(root, 'check')}<span class="sr-only">Unlocked</span></span>`;
    } else if (canJump) {
      status = `<span class="cart-offers-list__state cart-offers-list__state--link" aria-hidden="true">${iconMarkup(root, 'arrow')}</span>`;
    } else if (!row.jump) {
      status = `<span class="cart-offers-list__state">${iconMarkup(root, 'lock')}<span class="sr-only">Locked</span></span>`;
    }
    const inner = `
        ${media}
        <div class="cart-offers-list__text">
          <p class="cart-offers-list__title subheading">${escapeHtml(row.title)}</p>
          ${row.message ? `<p class="cart-offers-list__message">${escapeHtml(row.message)}</p>` : ''}
        </div>
        ${status}`;
    const state = row.unlocked ? 'unlocked' : row.jump ? 'available' : 'locked';
    const rowEl = canJump
      ? `<button type="button" class="cart-offers-list__row" data-offers-jump aria-label="${escapeHtml(row.title)}: ${escapeHtml(row.message)}. Show the reduced products">${inner}</button>`
      : `<div class="cart-offers-list__row">${inner}</div>`;

    return `
      <li class="cart-offers-list__item" data-offer-id="${escapeHtml(row.id)}" data-state="${state}">${rowEl}</li>`;
  }

  function editorBlockIds() {
    return Array.from(document.querySelectorAll('#cart-drawer [data-cart-offer-block-ids]'))
      .flatMap((element) => element.dataset.cartOfferBlockIds.split(',').filter(Boolean));
  }

  // Tokens: o:<id> (offer exists), u:<id> (offer unlocked), b:<id> (editor offer block).
  function signatureFor(rows) {
    const tokens = [
      ...rows.map((row) => `o:${row.id}`),
      ...rows.filter((row) => row.unlocked).map((row) => `u:${row.id}`),
      ...editorBlockIds().map((id) => `b:${id}`),
    ];
    return Array.from(new Set(tokens)).sort().join('|');
  }

  // Unseen = the live set has something the stored set lacks (a new offer, block, or unlock).
  // Removals and re-locks don't light the dot.
  function hasUnseen() {
    if (liveSignature === null) return false;
    const seen = new Set((readSeen() || '').split('|'));
    return liveSignature.split('|').some((token) => token && !seen.has(token));
  }

  function offersTabActive() {
    return document.querySelector('#cart-drawer x-tabs.tab-pills button[data-drawer-tab="offers"]')?.getAttribute('aria-selected') === 'true';
  }

  function drawerOpen() {
    return Boolean(document.getElementById('cart-drawer')?.open);
  }

  function updateDots() {
    const unseen = hasUnseen();
    document.querySelectorAll('#cart-drawer .tab-pills__dot-indicator--tracked').forEach((dot) => {
      dot.classList.toggle('has-unseen', unseen);
      const label = dot.querySelector('[data-offers-unseen-label]');
      if (label) label.textContent = unseen ? ', new offers' : '';
    });
  }

  function markSeen() {
    if (liveSignature === null) return;
    writeSeen(liveSignature);
    updateDots();
  }

  function markSeenIfViewing() {
    if (drawerOpen() && offersTabActive()) markSeen();
  }

  async function render() {
    const request = ++renderRequest;
    const roots = Array.from(document.querySelectorAll('#cart-drawer [data-cart-offers-list]'));

    if (!roots.length) {
      // No Windfall list in the tab (editor blocks only) — signature is just the blocks.
      liveSignature = document.querySelector('#cart-drawer .tab-pills__dot-indicator--tracked') ? signatureFor([]) : null;
      updateDots();
      markSeenIfViewing();
      return;
    }

    let cart;
    try {
      cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin', cache: 'no-store' })).json();
    } catch {
      return; // keep whatever is painted
    }
    if (request !== renderRequest) return;

    const { rows, contractLoaded } = buildRows(roots[0], cart);
    if (!contractLoaded) return; // keep the Liquid title-only fallback

    roots.forEach((root) => {
      const list = root.querySelector('[data-cart-offers-rows]');
      if (list) list.innerHTML = rows.map((row) => rowMarkup(root, row)).join('');
      root.hidden = rows.length === 0;
    });

    liveSignature = signatureFor(rows);
    updateDots();
    markSeenIfViewing();
  }

  function refresh() {
    if (scheduled) return;
    scheduled = true;
    // setTimeout (not rAF) so hidden/background tabs still update; batches same-tick events.
    setTimeout(() => {
      scheduled = false;
      render();
    }, 0);
  }

  // ---------------------------------------------------------------- wiring

  document.addEventListener('windfall:offers:change', refresh);
  document.addEventListener('cart-drawer:refreshed', refresh);
  ['cart:change', 'cart:refresh', 'cart:updated'].forEach((name) => document.addEventListener(name, refresh));

  // dialog:after-show doesn't bubble; the capture phase still reaches document.
  document.addEventListener('dialog:after-show', (event) => {
    if (event.target?.id === 'cart-drawer') markSeenIfViewing();
  }, true);

  document.addEventListener('click', (event) => {
    if (event.target.closest('#cart-drawer [data-cart-v2-tab-index="2"], #cart-drawer button[data-drawer-tab="offers"]')) {
      setTimeout(markSeenIfViewing, 0);
    }
    if (event.target.closest('#cart-drawer [data-offers-jump]')) {
      const target = jumpTarget();
      if (!target) return;
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!event.target.matches?.('#cart-drawer x-tabs.tab-pills button[role="tab"]')) return;
    setTimeout(markSeenIfViewing, 0);
  });

  // The embed may finish loading after this script; re-render once it exposes its API.
  let attempts = 0;
  const waitForEmbed = setInterval(() => {
    if (window.Windfall?.getContract?.() || ++attempts > 40) {
      clearInterval(waitForEmbed);
      refresh();
    }
  }, 250);

  window[key] = { refresh, markSeen };
  refresh();
})();
